/*
 * Copyright (C) 2019 SADE Innovations Oy - All Rights Reserved
 *
 * NOTICE: This software is owned by SADE Innovations Oy and licensed under SADE Booster license.
 * All dissemination, usage, modification, copying, reproduction, selling and distribution of the
 * software and its intellectual and technical concepts are strictly forbidden without a valid license.
 * Such license can be obtained by issuing a SADE Booster License agreement from SADE Innovations Oy
 * (https://sadeinnovations.com).
 *
 */

import React, { BaseSyntheticEvent, Component, Fragment, ReactNode } from "react";
import ErrorDialog from "../../../ui/error-dialog";
import FormDialog from "../../../ui/form-dialog";
import {
  Button,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Grid,
  TextField,
  Typography,
} from "@material-ui/core";
import Loader from "../../../ui/loader";
import { Organization, User, EventDefinition, BackendFactory, OtaUpdate, OtaManager } from "@sade/data-access";
import UpdateIcon from "@material-ui/icons/Update";
import { translations } from "../../../../generated/translationHelper";
import DropdownSelection from "../../../ui/dropdown-selection";
import SelectOtaUpdatePopup from "./select-ota-update-popup";
import { Maybe } from "@sade/data-access/lib/common/Utils";
import Checkbox from "@material-ui/core/Checkbox";

interface Props {
  organization: Organization;
  users: User[];
  events: EventDefinition[];
  open: boolean;
  onClose: (success: boolean) => void;
}

interface State {
  loading: boolean;
  selectedDeviceType?: string;
  deviceTypes: string[];
  errorMessage?: string;
  searchQuery: string;
  searchResults?: number;
  selectedFirmware?: OtaUpdate;
  selectedFirmwareVersion: string;
  showSelectOtaUpdatePopup: boolean;
  selectedMaximum: string;
  batch1Count: string;
  batch2Count: string;
  batch3Count: string;
  notifyTime: string;
  batchTotal: string;
  onlineDevicesOnly: boolean;
}

export default class AddBatchUpdatePopup extends Component<Props, State> {
  public constructor(props: Props) {
    super(props);
    this.state = {
      loading: false,
      deviceTypes: BackendFactory.getBackend().getSupportedDeviceTypes(),
      searchQuery: "",
      showSelectOtaUpdatePopup: false,
      selectedFirmwareVersion: "",
      batch1Count: "0",
      batch2Count: "0",
      batch3Count: "0",
      notifyTime: "24",
      batchTotal: "0",
      selectedMaximum: "",
      onlineDevicesOnly: false,
    };
  }

  private getFullSearchQuery(): string {
    if (!this.state.selectedFirmware) throw new Error(translations.admin.texts.noFirmwareSelected());
    const query = `thingTypeName:${this.state.selectedDeviceType} NOT shadow.reported.deviceFwVersion:${
      this.state.selectedFirmware.firmwareVersion
    }${this.state.searchQuery ? " AND " + this.state.searchQuery : ""}`;
    if (this.state.onlineDevicesOnly) {
      return query + " AND connectivity.connected:true";
    }
    return query;
  }

  private searchDevices = async (): Promise<void> => {
    if (!this.state.selectedDeviceType) {
      return this.setErrorState(translations.admin.texts.noDeviceTypeSelected());
    }

    try {
      this.setState({ loading: true });
      const devicesCount = await BackendFactory.getBackend().countDevices(this.getFullSearchQuery());
      this.setState({
        searchResults: devicesCount,
        selectedMaximum: devicesCount.toString(),
        batch1Count: devicesCount.toString(),
        batch2Count: "0",
        batch3Count: "0",
        notifyTime: "24",
        batchTotal: devicesCount.toString(),
      });
    } catch (error) {
      this.setErrorState(error instanceof Error ? error : JSON.stringify(error));
    } finally {
      this.setState({ loading: false });
    }
  };

  private selectFirmware(): void {
    if (this.state.selectedDeviceType) {
      this.setState({ showSelectOtaUpdatePopup: true });
    }
  }

  private closeThis(success: boolean): void {
    this.setState({
      selectedFirmwareVersion: "",
      selectedDeviceType: undefined,
      selectedFirmware: undefined,
      searchQuery: "",
      searchResults: undefined,
    });
    this.props.onClose(success);
  }

  private createBatch = async (): Promise<void> => {
    this.setState({ loading: true });

    if (!this.state.searchResults) {
      return this.setErrorState(translations.admin.texts.noSearchResults());
    }

    if (!this.state.selectedFirmware) {
      return this.setErrorState(translations.admin.texts.noFirmwareSelected());
    }

    if (!this.state.selectedDeviceType) {
      return this.setErrorState(translations.admin.texts.noDeviceTypeSelected());
    }

    try {
      await OtaManager.getInstance().createOtaBatchSession(
        [parseInt(this.state.batch1Count), parseInt(this.state.batch2Count), parseInt(this.state.batch3Count)],
        Number(this.state.notifyTime),
        this.state.selectedFirmware.otaId,
        this.state.selectedFirmware.firmwareVersion,
        this.state.selectedDeviceType,
        this.getFullSearchQuery()
      );
    } catch (error) {
      this.setErrorState(error instanceof Error ? error : JSON.stringify(error));
    } finally {
      this.setState({ loading: false });
      this.closeThis(true);
    }
  };

  private setErrorState(message: string | Error): void {
    console.error("AddBatchUpdatePopup", message);
    this.setState({
      errorMessage: message.toString(),
    });
  }

  private renderError(): ReactNode {
    return (
      <ErrorDialog
        onClose={(): void => {
          this.setState({ errorMessage: undefined });
        }}
        errorMsg={this.state.errorMessage}
      />
    );
  }

  private countTotal = (event: BaseSyntheticEvent): void => {
    console.log(event.currentTarget);
    if (event.currentTarget.id === "batch-1") {
      this.setState({
        batch1Count: event.currentTarget.value,
        batchTotal: (
          parseInt(event.currentTarget.value) +
          parseInt(this.state.batch2Count) +
          parseInt(this.state.batch3Count)
        ).toString(),
      });
    } else if (event.currentTarget.id === "batch-2") {
      this.setState({
        batch2Count: event.currentTarget.value,
        batchTotal: (
          parseInt(this.state.batch1Count) +
          parseInt(event.currentTarget.value) +
          parseInt(this.state.batch3Count)
        ).toString(),
      });
    } else if (event.currentTarget.id === "batch-3") {
      this.setState({
        batch3Count: event.currentTarget.value,
        batchTotal: (
          parseInt(this.state.batch1Count) +
          parseInt(this.state.batch2Count) +
          parseInt(event.currentTarget.value)
        ).toString(),
      });
    }
  };

  private handleClosePopup = (): void => {
    this.setState({
      showSelectOtaUpdatePopup: false,
    });
  };

  private getCurrentDeviceTypeSelectionIndex(): Maybe<number> {
    const index = this.state.deviceTypes.findIndex((deviceType) => deviceType === this.state.selectedDeviceType);
    return index !== -1 ? index : undefined;
  }

  private handleDeviceTypeSelected = (index?: number): void => {
    this.setState({
      selectedDeviceType: index !== undefined ? this.state.deviceTypes[index] : undefined,
    });
  };

  private renderSelectOtaUpdatePopup(): ReactNode {
    if (this.state.selectedDeviceType) {
      return (
        <SelectOtaUpdatePopup
          open={this.state.showSelectOtaUpdatePopup}
          type={this.state.selectedDeviceType}
          onClose={async (selection): Promise<void> => {
            this.handleClosePopup();

            if (selection) {
              console.log("selected " + selection.firmwareVersion);
              this.setState({ selectedFirmware: selection, selectedFirmwareVersion: selection.firmwareVersion });
            }
          }}
        />
      );
    }
  }

  private onOnlineDevicesOnlyCheckboxChange = (event: React.FormEvent<HTMLInputElement>): void => {
    this.setState({ onlineDevicesOnly: event.currentTarget.checked });
  };

  public render(): ReactNode {
    return (
      <Fragment>
        {this.renderError()}
        {this.renderSelectOtaUpdatePopup()}
        <FormDialog
          title={translations.admin.texts.newBatchUpdate()}
          acceptButtonText={translations.admin.texts.createBatch()}
          isOpen={this.props.open}
          onAccept={this.createBatch}
          onCancel={(): void => this.closeThis(false)}
          disableAccept={
            !this.state.searchResults ||
            !this.state.selectedFirmware ||
            Number(this.state.batchTotal) !== this.state.searchResults
          }
          maxWidth="md"
        >
          <UpdateIcon className="notification-organization-icon" />
          <Typography>{this.props.organization.getName()}</Typography>
          <FormControl component="fieldset" margin="normal" fullWidth>
            <FormLabel component="legend">{translations.admin.texts.selectDeviceType()}</FormLabel>
            <DropdownSelection
              currentSelection={this.getCurrentDeviceTypeSelectionIndex()}
              onSelect={this.handleDeviceTypeSelected}
              selectionList={this.state.deviceTypes.map((deviceType) => ({ key: deviceType, label: deviceType }))}
              emptySelectionItem={"Device Type"}
              disabled={this.state.deviceTypes?.length === 0}
            />
          </FormControl>
          <FormControl component="fieldset" margin="normal" fullWidth>
            <TextField
              id="query-label"
              label={translations.admin.texts.selectFirmware()}
              variant="standard"
              value={this.state.selectedFirmwareVersion}
              onClick={(): void => void this.selectFirmware()}
              disabled={!this.state.selectedDeviceType}
              InputProps={{
                readOnly: true,
                classes: {
                  underline: "input-readonly underlined",
                  focused: "input-readonly focused",
                },
              }}
            />
          </FormControl>
          <FormControl component="fieldset" margin="normal" fullWidth>
            <TextField
              id="query-label"
              label={translations.admin.texts.query()}
              variant="standard"
              value={this.state.searchQuery ?? ""}
              onChange={(event): void =>
                this.setState({ searchQuery: event.currentTarget.value, searchResults: undefined })
              }
              disabled={!this.state.selectedFirmware}
            />
          </FormControl>
          <FormControl component="fieldset" margin="normal" fullWidth>
            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox
                    id="onlidDevicesOnly-checkbox"
                    checked={this.state.onlineDevicesOnly}
                    onChange={this.onOnlineDevicesOnlyCheckboxChange}
                    color="primary"
                  />
                }
                label={translations.admin.texts.onlineDevicesOnly()}
                labelPlacement="end"
              />
            </FormGroup>
          </FormControl>
          <FormControl component="fieldset" margin="normal" fullWidth>
            <Button
              variant="contained"
              color="primary"
              onClick={this.searchDevices}
              disabled={!this.state.selectedFirmware}
            >
              {translations.common.inputs.search()}
            </Button>
          </FormControl>
          <div style={{ height: "180px", width: "100%" }}>
            {!this.state.loading && this.state.searchResults !== undefined && (
              <Fragment>
                <Grid container={true} direction={"row"} justifyContent="space-evenly">
                  <Typography style={{ paddingTop: "30px" }}>
                    {translations.admin.texts.devicesFound({ count: this.state.searchResults })}
                  </Typography>
                </Grid>
                <Grid container={true} direction={"row"} justifyContent="space-evenly" alignItems="center">
                  <Grid item={true}>
                    <FormControl component="fieldset" margin="normal">
                      <TextField
                        id="batch-1"
                        label={translations.admin.texts.batch1Label()}
                        variant="standard"
                        type="number"
                        value={this.state.batch1Count}
                        onChange={(event): void => this.countTotal(event)}
                        disabled={!this.state.searchResults}
                      />
                    </FormControl>
                  </Grid>
                  <Grid>
                    <Typography>+</Typography>
                  </Grid>
                  <Grid item={true}>
                    <FormControl component="fieldset" margin="normal">
                      <TextField
                        id="batch-2"
                        label={translations.admin.texts.batch2Label()}
                        variant="standard"
                        type="number"
                        value={this.state.batch2Count}
                        onChange={(event): void => this.countTotal(event)}
                        disabled={!this.state.searchResults}
                      />
                    </FormControl>
                  </Grid>
                  <Grid>
                    <Typography>+</Typography>
                  </Grid>
                  <Grid item={true}>
                    <FormControl component="fieldset" margin="normal">
                      <TextField
                        id="batch-3"
                        label={translations.admin.texts.batch3Label()}
                        variant="standard"
                        type="number"
                        value={this.state.batch3Count}
                        onChange={(event): void => this.countTotal(event)}
                        disabled={!this.state.searchResults}
                      />
                    </FormControl>
                  </Grid>
                  <Grid>
                    <Typography>=</Typography>
                  </Grid>
                  <Grid>
                    <FormControl component="fieldset" margin="normal">
                      <TextField
                        id="totallbl"
                        label={translations.admin.texts.totalLabel()}
                        variant="standard"
                        type="number"
                        value={this.state.batchTotal}
                        disabled={!this.state.searchResults}
                      />
                    </FormControl>
                  </Grid>
                  <Grid container={true} direction={"row"} justifyContent="space-evenly">
                    <FormControl component="fieldset" margin="normal">
                      <TextField
                        id="notifyTime"
                        label={translations.admin.texts.notifyTime()}
                        variant="standard"
                        type="number"
                        value={this.state.notifyTime}
                        onChange={(event): void => this.setState({ notifyTime: event.currentTarget.value })}
                        disabled={!this.state.searchResults}
                      />
                    </FormControl>
                  </Grid>
                </Grid>
              </Fragment>
            )}

            <Loader show={this.state.loading} />
          </div>
        </FormDialog>
      </Fragment>
    );
  }
}
